
"use client";

import React, { useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  useNodesState, 
  useEdgesState, 
  addEdge, 
  Connection, 
  Edge, 
  Node, 
  ReactFlowProvider 
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getAccountOwnershipInfo } from "./business-value-framework/accountService";
import { setNavigationSourceForBusinessValue } from "./business-value-framework/navigationService";

// Import components
import KpiInputPanel from "./business-value-framework/KpiInputPanel";
import FlowComponent from "./business-value-framework/FlowComponent";
import CustomNode from "./business-value-framework/CustomNode";
import LabelNode from "./business-value-framework/LabelNode";

// Import utilities
import { 
  validateAndConvertEdges, 
  calculateRectangleWidth, 
  initialNodes,
  KpiData
} from "./business-value-framework/utils";

// Import services
import { 
  saveNodeEdit,
  fetchFramework,
  addKPINode
} from "./business-value-framework/frameworkService";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

const BusinessValueFrameworkTab = ({
  selectedAccount
}: {
  selectedAccount: Account | null;
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [kpiType, setKpiType] = useState("");
  const [kpiTitle, setKpiTitle] = useState("");
  const [kpiDescription, setKpiDescription] = useState("");
  const [nodeTypes, setNodeTypes] = useState({});
  const [flowHeight, setFlowHeight] = useState(440);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hasLoadedKpiData, setHasLoadedKpiData] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [ownershipLoaded, setOwnershipLoaded] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    
    getUser();
  }, []);

  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const parentHeight = containerRef.current.parentElement?.clientHeight || 0;
        const controlsHeight = 120;
        const availableHeight = Math.max(parentHeight - controlsHeight, 300);
        setFlowHeight(availableHeight);
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  // Set node types
  useEffect(() => {
    setNodeTypes({
      default: CustomNode,
      label: LabelNode
    });
  }, []);

  // Check if user can edit
  useEffect(() => {
    const checkEditPermission = async () => {
      if (!selectedAccount?.company_id || !user) return;
      
      try {
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        setCanEdit(ownershipInfo.canEdit);
        
        // Only set as supervisor if user can view but is not the owner
        setIsSupervisor(ownershipInfo.canView && !ownershipInfo.isOwner);
        setOwnershipLoaded(true);
      } catch (error) {
        console.error('Error checking edit permission:', error);
        setCanEdit(false);
        setOwnershipLoaded(false);
      }
    };

    checkEditPermission();
  }, [selectedAccount, user]);

  // Handle node double click
  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, nodeId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    console.log("Node double-clicked in handler:", nodeId);

    if (nodeId.startsWith('kpi-')) {
      setSelectedNodeId(nodeId);
      console.log("Selected node ID set to:", nodeId);
    }
  }, []);

  // Handle node selection change
  const handleSelectionChange = useCallback(({ nodes: selectedNodes }: { nodes: Node[] }) => {
    const selectedKpiNode = selectedNodes.find(node => node.id.startsWith('kpi-'));
    if (selectedKpiNode) {
      setSelectedNodeId(selectedKpiNode.id);
    } else {
      setSelectedNodeId(null);
    }
  }, []);

  // Update nodes to include necessary data and handlers
  useEffect(() => {
    if (nodes.length > 0) {
      setNodes(currentNodes => 
        currentNodes.map(node => {
          if (node.id.startsWith('kpi-')) {
            return {
              ...node,
              selected: node.id === selectedNodeId,
              data: {
                ...node.data,
                onNodeDoubleClick: handleNodeDoubleClick,
                isSelected: node.id === selectedNodeId,
              }
            };
          }
          return node;
        })
      );
    }
  }, [nodes.length, selectedNodeId, handleNodeDoubleClick, setNodes]);

  // Save node edits wrapper function
  const handleSaveNodeEdit = useCallback(async (title: string, description: string) => {
    if (!selectedAccount?.company_id) return;
    
    await saveNodeEdit(
      selectedNodeId, 
      title, 
      description, 
      selectedAccount.company_id, 
      setNodes, 
      handleNodeDoubleClick
    );
    
    setSelectedNodeId(null);
  }, [selectedNodeId, selectedAccount, setNodes, handleNodeDoubleClick]);

  const updateRectangleWidths = useCallback((currentNodes: Node[]) => {
    const areaYPositions = [25, 135, 245, 355, 465];
    const updatedNodes = currentNodes.map(node => {
      if (node.type === 'group') {
        const yPos = node.position.y + 25;
        const newWidth = calculateRectangleWidth(currentNodes, yPos);
        return {
          ...node,
          style: {
            ...node.style,
            width: newWidth
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [setNodes]);

  const onConnect = useCallback(async (params: Connection) => {
    if (params.sourceHandle === 'bottom' && params.targetHandle === 'top' || 
        params.sourceHandle === 'top' && params.targetHandle === 'bottom') {
      const newEdges = addEdge(params, edges);
      setEdges(newEdges);
      if (selectedAccount?.company_id) {
        try {
          // Get account ownership info
          const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
          if (!ownershipInfo.canEdit) {
            toast.error("Only account owners can create connections");
            return;
          }
          
          // Ensure edges are properly serialized for Supabase
          const serializableEdges = JSON.parse(JSON.stringify(newEdges));
          const { error } = await supabase
            .from('business_value_framework')
            .update({ edges: serializableEdges })
            .eq('company_id', selectedAccount.company_id)
            .eq('user_id', ownershipInfo.accountOwnerUserId);
            
          if (error) {
            console.error('Error saving edges:', error);
            toast.error("Failed to save connection");
          } else {
            console.log('Successfully saved edges to Supabase');
          }
        } catch (error) {
          console.error('Error saving edges:', error);
          toast.error("Failed to save connection");
          error?.message && console.error(error.message);
        }
      }
    }
  }, [edges, selectedAccount, setEdges]);

  const onNodesChange2 = useCallback(async (changes: any) => {
    onNodesChange(changes);

    // Only save positions after dragging is complete
    const positionChanges = changes.filter((change: any) => 
      change.type === 'position' && change.dragging === false
    );
    
    if (positionChanges.length > 0 && selectedAccount?.company_id) {
      try {
        const updatedNodes = nodes.map(node => {
          const change = positionChanges.find((c: any) => c.id === node.id);
          if (change) {
            return {
              ...node,
              position: change.position
            };
          }
          return node;
        });
        
        updateRectangleWidths(updatedNodes);
        
        // Get account ownership info
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        if (!ownershipInfo.canEdit) {
          toast.error("Only account owners can move nodes");
          return;
        }
        
        // Save each changed node's position to the database using account owner's user_id
        for (const change of positionChanges) {
          if (change.id.startsWith('kpi-')) {
            const nodeIndex = nodes.findIndex(n => n.id === change.id);
            if (nodeIndex >= 0) {
              const { data: existingNodes, error: fetchError } = await supabase
                .from('business_value_framework')
                .select('id')
                .eq('company_id', selectedAccount.company_id)
                .eq('user_id', ownershipInfo.accountOwnerUserId)
                .order('created_at', { ascending: true });
                
              if (fetchError) {
                console.error('Error fetching existing nodes:', fetchError);
                continue;
              }
              
              let dbNodeId = null;
              if (existingNodes && existingNodes[nodeIndex]) {
                dbNodeId = existingNodes[nodeIndex].id;
              }
              
              if (dbNodeId) {
                const { error } = await supabase
                  .from('business_value_framework')
                  .update({
                    position_x: change.position.x,
                    position_y: change.position.y
                  })
                  .eq('id', dbNodeId)
                  .eq('company_id', selectedAccount.company_id)
                  .eq('user_id', ownershipInfo.accountOwnerUserId);
                  
                if (error) {
                  console.error('Error updating node position:', error);
                  toast.error("Failed to save node position");
                } else {
                  console.log('Successfully saved node position for', dbNodeId);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error updating node positions:', error);
        toast.error("Failed to save node positions");
      }
    }
  }, [nodes, selectedAccount, onNodesChange, updateRectangleWidths]);

  const handleEdgesChange = useCallback(async (changes: any) => {
    onEdgesChange(changes);
    
    if (selectedAccount?.company_id) {
      try {
        // Get account ownership info
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        if (!ownershipInfo.canEdit) {
          toast.error("Only account owners can modify connections");
          return;
        }
        
        let newEdges = [...edges];
        
        changes.forEach((change: any) => {
          if (change.type === 'remove') {
            newEdges = newEdges.filter(edge => edge.id !== change.id);
          }
        });
        
        // Ensure edges are properly serialized for Supabase
        const serializableEdges = JSON.parse(JSON.stringify(newEdges));
        const { error } = await supabase
          .from('business_value_framework')
          .update({ edges: serializableEdges })
          .eq('company_id', selectedAccount.company_id)
          .eq('user_id', ownershipInfo.accountOwnerUserId);
          
        if (error) {
          console.error('Error saving edges:', error);
          toast.error("Failed to save edge changes");
        } else {
          console.log('Successfully saved edge changes to Supabase');
        }
      } catch (error) {
        console.error('Error saving edges:', error);
        toast.error("Failed to save edge changes");
      }
    }
  }, [edges, selectedAccount, onEdgesChange]);

  // Add KPI node handler
  const handleAddKPINode = async () => {
    if (!selectedAccount?.company_id) return;
    
    const success = await addKPINode(
      kpiType,
      kpiTitle,
      kpiDescription,
      selectedAccount,
      nodes,
      setNodes,
      updateRectangleWidths,
      handleNodeDoubleClick
    );
    
    if (success) {
      setKpiTitle("");
      setKpiDescription("");
    }
  };

  // Load data when account changes and set navigation source
  useEffect(() => {
    if (selectedAccount?.company_id) {
      setNodes(initialNodes);
      setEdges([]);
      setHasLoadedKpiData(false);
      
      // Set navigation source for KPI creation behavior
      setNavigationSourceForBusinessValue();
      
      fetchFramework(
        selectedAccount.company_id,
        setNodes,
        setEdges,
        updateRectangleWidths,
        handleNodeDoubleClick,
        setHasLoadedKpiData
      );
    }
  }, [selectedAccount, setNodes, setEdges, updateRectangleWidths, handleNodeDoubleClick]);

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 my-[12px] h-[600px]">
      <CardContent className="mt-4 bg-secondary/50 h-[570px] my-[12px] border-none">
        <div className="flex justify-between items-center mb-4">
          {selectedAccount && (
            <p className="text-[11px] text-primary-light my-0 py-0">
              Selected Account ID: {selectedAccount.company_id} ({selectedAccount.company_name})
            </p>
          )}
        </div>

        {ownershipLoaded && isSupervisor && (
          <div className="mb-2 p-1 bg-primary/20 text-primary border-primary/30 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-secondary-800">
                You are viewing this account as a supervisor. Only the account owner can modify data in the business value framework.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4" ref={containerRef}>
          <KpiInputPanel
            kpiType={kpiType}
            setKpiType={setKpiType}
            kpiTitle={kpiTitle}
            setKpiTitle={setKpiTitle}
            kpiDescription={kpiDescription}
            setKpiDescription={setKpiDescription}
            addKPINode={handleAddKPINode}
            isDisabled={!canEdit}
          />

          <div className="flex">
            <div className="flex-1">
              <ReactFlowProvider>
                <FlowComponent
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange2}
                  onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  flowHeight={flowHeight}
                  onSelectionChange={handleSelectionChange}
                  selectedAccount={selectedAccount}
                />
              </ReactFlowProvider>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BusinessValueFrameworkTab;
